// The first application: a local, community-governed marketplace, written purely as policy DATA.
// Every marketplace word (offer, accept, rate, report, decide…) lives here as a predicate name.
// The kernel (claim.ts, rules.ts, scope.ts) has never heard of any of them.
//
// v2 closes the red team's policy gaps (REDTEAM.md rows 14, 16, 18, 20) and the UX requests R1, R4, R5, R6, R7, R8,
// R10, R16 (ux/MODEL-REQUESTS.md). Every clause carries a stable label (R1): explanations key on labels, not positions.

import type { ActorId } from '../claim.ts';
import type { C, Filter, Policy, V } from '../rules.ts';

const DAY = 86_400_000;

// A labelled clause. Labels are the stable names a UI maps to plain language; positions may change.
const L = <T extends Exclude<C, true>>(label: string, c: T): T => ({ ...c, label });

const member = (who: V): C => ({ exists: { predicate: 'join', author: who } });

// Graded identity: an attested member holds a phone attestation from an issuer the policy trusts.
const attested = (who: V): C => ({ exists: { predicate: 'verified.phone', subject: who, authorIn: { param: 'phoneIssuers' } } });

// A standing sanction: a decision with this outcome, not yet lapsed, and not superseded by a later decision. Only an
// appeal decision may mention a decision (refs = [appeal, decision]; decide.reports quantifies every ref as a report),
// so `mentionedBy: ['decide']` is exactly "overturned or re-decided on appeal".
const activeSanction = (who: V, outcome: string): Filter => ({
  predicate: 'decide',
  subject: who,
  mentionedBy: ['decide'],
  where: [
    { field: 'body.outcome', op: 'eq', value: { lit: outcome } },
    { field: 'body.until', op: 'gt', value: '$now' },
  ],
});

// Rolling window: how many claims of `predicate` this author had admitted in the last `window` ms.
const recent = (predicate: string, window: string): Filter => ({ predicate, author: '$author', admittedSince: { sub: ['$now', { param: window }] } });

// Buyer of an accept = its author. Seller = author of the offer the accept points to.
const buyerOf = (accept: V): V => ({ get: 'author', of: accept });
const sellerOf = (accept: V): V => ({ get: 'author', of: { get: 'ref0', of: accept } });
const partyTo = (accept: V): C => ({ any: [{ eq: ['$author', buyerOf(accept)] }, { eq: ['$author', sellerOf(accept)] }] });

// Two actors are vouch-linked when either vouched for the other.
const vouchLinked = (a: V, b: V): C => ({
  any: [{ exists: { predicate: 'vouch', author: a, subject: b } }, { exists: { predicate: 'vouch', author: b, subject: a } }],
});

// An accept that still stands: nobody released it. A released accept frees its offer (R8).
const liveAccept = (offer: V): Filter => ({ predicate: 'accept', refs: offer, unrefBy: ['release'] });
// The same, as an unrefBy spec (the referencing claim is already bound; no `refs` key).
const standingAccept: Filter = { predicate: 'accept', unrefBy: ['release'] };

// The ramp (#18, R16). It lifts when the author has either
//   (a) received a rating (ratings between vouch-linked members are inadmissible, so a puppet's rating never lands), or
//   (b) a settled trade: both parties confirmed, the counterparty is not vouch-linked to the author, and `rampAfter`
//       has passed since the author's confirmation, so a counterparty who was wronged has had time to report.
// (b) is a bounded join (`some` over the author's completes, testing each one's counterparty). It exists because a
// newcomer whose buyer never rates must not stay at the newcomer limit for ever.
const acceptOf = (complete: V): V => ({ get: 'ref0', of: complete });
const counterpartyOf = (complete: V): V => ({ if: { eq: ['$author', buyerOf(acceptOf(complete))] }, then: sellerOf(acceptOf(complete)), else: buyerOf(acceptOf(complete)) });
const rampLifted: C = {
  any: [
    L('ramp.rated', { exists: { predicate: 'rate', subject: '$author' } }),
    L('ramp.settled-trade', {
      some: {
        all: [
          L('ramp.both-confirmed', { count: { predicate: 'complete', refs: acceptOf('$item') }, distinct: 'author', op: 'gte', n: { lit: 2 } }),
          L('ramp.independent-counterparty', { not: vouchLinked('$author', counterpartyOf('$item')) }),
        ],
      },
      over: { match: { predicate: 'complete', author: '$author', admittedBefore: { sub: ['$now', { param: 'rampAfter' }] } } },
    }),
  ],
};

// How many live offers an actor may hold: friction while reports are open, a ramp for newcomers.
const offerLimit: V = {
  if: L('friction.open-reports', {
    count: { predicate: 'report', subject: '$author', mentionedBy: ['decide'] }, // a decision lists every report it closes
    distinct: 'author',
    op: 'gte',
    n: { param: 'frictionReporters' },
  }),
  then: { param: 'frictionOfferLimit' },
  else: {
    if: rampLifted,
    then: { param: 'offerLimit' },
    else: { param: 'newcomerOfferLimit' },
  },
};

// #14: a promoted message must be a communication between the two parties to the case: the report's author and
// its subject must each be the message's author or in its audience. Third-party confidences stay out.
const embeddedParty = (who: V): C => ({
  any: [{ eq: [who, { get: 'body.$claim.author', of: '$self' }] }, { in: [who, { get: 'body.$claim.audience', of: '$self' }] }],
});

const steward: Exclude<C, true> = { in: ['$author', { param: 'stewards' }] };

// v2.1 (refs smuggling): the kernel now reads `refs` as refs[0] everywhere, so a second reference has no effect in any
// rule. Single-reference predicates refuse one anyway: a claim should not carry a pointer no rule reads.
const oneRef = (p: string): C => L(`${p}.one-ref`, { eq: [{ get: 'ref1', of: '$self' }, { lit: null }] });
const twoRefs = (p: string): C => L(`${p}.two-refs`, { eq: [{ get: 'ref2', of: '$self' }, { lit: null }] });

export interface MarketplaceSetup {
  phoneIssuers: ActorId[];
  stewards: ActorId[];
  triage: ActorId[];
  // L3′: the key that signs receipts. Defaults to the community key itself, except under `amendment` (v2.2, below).
  witness?: ActorId;
  // #20: when set, amending the policy needs `quorum` distinct council approvals of a proposal; the community key
  // alone can no longer rewrite the rules.
  // v2.2 (REDTEAM-3 G5): `amendment` requires an explicit `witness`. Under a quorum the community key must not keep the
  // power to date receipts (and so to end sanctions) alone; who holds the witness key is founder decision 2. The safe
  // default here is only that it is NAMED, so a deployment cannot fall back to the community key by omission.
  amendment?: { council: ActorId[]; quorum: number };
}

export function marketplacePolicy(s: MarketplaceSetup): Policy {
  if (s.amendment && !s.witness) throw new Error('marketplacePolicy: amendment needs an explicit witness key (params.witness), distinct from the community key');
  const params: Policy['params'] = {
    phoneIssuers: s.phoneIssuers,
    stewards: s.stewards,
    triage: s.triage,
    newcomerOfferLimit: 3,
    offerLimit: 10,
    frictionReporters: 2,
    frictionOfferLimit: 1,
    // #18 caps
    vouchCap: 3,
    vouchWindow: 30 * DAY,
    reportCap: 3,
    reportWindow: DAY,
    // R16: a settled trade lifts the ramp this long after the author's confirmation
    rampAfter: 7 * DAY,
    // v2.2 (REDTEAM-3 U3): how many accepts a buyer may hold open (not released, not yet confirmed by anyone) at once
    acceptCap: 3,
    // v2.2 (REDTEAM-3 K2): appeal outcomes that lift a sanction. Any other appeal outcome must restate the original.
    liftOutcomes: ['overturned', 'no-action'],
    // v2.1 (g): these claims must carry scope = this community; the kernel refuses them unbound (wrong-scope), so a
    // report, a sanction, an appeal or a vouch cannot be forwarded from a sibling scope.
    boundPredicates: ['report', 'decide', 'appeal', 'vouch'],
  };
  if (s.witness) params.witness = s.witness;
  if (s.amendment) {
    params.council = s.amendment.council;
    params.quorum = s.amendment.quorum;
    // v2.2 (REDTEAM-3 G2): proposals and approvals are bound to this community, so a council shared with a sibling
    // cannot have its amendment there forwarded and enacted here.
    params.boundPredicates = [...(params.boundPredicates as string[]), 'propose', 'approve'];
  }

  // Amending the rules is a claim like any other. Default: only the community actor may issue one.
  // #20 (optional): a proposal by a council member, `quorum` distinct council approvals, then anyone may enact the
  // byte-identical body. The scope key is not special.
  const policyRule: C = s.amendment
    ? {
        all: [
          L('policy.proposed', { is: { claim: '$ref', predicate: 'propose' } }),
          L('policy.quorum', { count: { predicate: 'approve', refs: '$ref', authorIn: { param: 'council' } }, distinct: 'author', op: 'gte', n: { param: 'quorum' } }),
          L('policy.as-proposed', { eq: [{ get: 'body', of: '$self' }, { get: 'body.policy', of: '$ref' }] }),
          // v2.2 (REDTEAM-3 G1): a proposal is enacted at most once, and only against the policy it was proposed under:
          // once any policy is admitted after the proposal, the proposal is spent. Without this anyone could re-enact
          // any policy the council ever approved (a rollback) with a fresh claim.
          L('policy.once', { not: { exists: { predicate: 'policy', refs: '$ref' } } }),
          L('policy.current', { not: { exists: { predicate: 'policy', admittedSince: { get: 'admittedAt', of: '$ref' } } } }),
        ],
      }
    : L('policy.by-community', { eq: ['$author', { param: 'scope' }] });

  const governance: Record<string, C> = s.amendment
    ? {
        propose: L('propose.council', { in: ['$author', { param: 'council' }] }),
        approve: {
          all: [
            L('approve.council', { in: ['$author', { param: 'council' }] }),
            L('approve.proposal', { is: { claim: '$ref', predicate: 'propose' } }),
            L('approve.once', { not: { exists: { predicate: 'approve', refs: '$ref', author: '$author' } } }),
          ],
        },
      }
    : {};

  return {
    params,
    rules: {
      policy: policyRule,
      ...governance,

      // Issuers attest facts about actors without storing documents (verify and forget).
      'verified.phone': {
        all: [L('phone.issuer', { in: ['$author', { param: 'phoneIssuers' }] }), L('phone.subject', { not: { eq: ['$subject', { lit: null }] } })],
      },

      // Joining needs one external attestation or one vouch from an existing member.
      join: {
        all: [
          L('join.not-member', { not: member('$author') }),
          L('join.attested-or-vouched', {
            any: [
              { exists: { predicate: 'verified.phone', subject: '$author', authorIn: { param: 'phoneIssuers' } } },
              { exists: { predicate: 'vouch', subject: '$author' } },
            ],
          }),
        ],
      },
      // #16: a vouch is bound to this scope (replaying it elsewhere fails). #18: only attested members vouch, so a
      // chain of vouches is cut at depth one, and each member vouches at most vouchCap times per vouchWindow.
      vouch: {
        all: [
          L('vouch.member', member('$author') as Exclude<C, true>),
          L('vouch.not-self', { not: { eq: ['$subject', '$author'] } }),
          L('vouch.first-for-subject', { not: { exists: { predicate: 'vouch', author: '$author', subject: '$subject' } } }),
          L('vouch.scope-bound', { eq: [{ get: 'scope', of: '$self' }, { param: 'scope' }] }),
          L('vouch.attested', attested('$author') as Exclude<C, true>),
          L('vouch.cap', { count: recent('vouch', 'vouchWindow'), op: 'lt', n: { param: 'vouchCap' } }),
        ],
      },

      // An offer passes through the capability gate: membership, no active sanction, under the live-offer limit.
      offer: {
        all: [
          L('offer.member', member('$author') as Exclude<C, true>),
          L('offer.not-paused', { not: { exists: activeSanction('$author', 'suspend-offers') } }),
          L('offer.under-limit', { count: { predicate: 'offer', author: '$author', unrefBy: ['withdraw', standingAccept] }, op: 'lt', n: offerLimit }),
        ],
      },
      withdraw: {
        all: [L('withdraw.offer', { is: { claim: '$ref', predicate: 'offer' } }), L('withdraw.own', { eq: ['$author', { get: 'author', of: '$ref' }] }), oneRef('withdraw')],
      },

      // A permissioned channel: the inquiry is visible only to the two parties (its audience).
      inquire: {
        all: [
          L('inquire.member', member('$author') as Exclude<C, true>),
          L('inquire.offer', { is: { claim: '$ref', predicate: 'offer' } }),
          L('inquire.not-own', { not: { eq: ['$author', { get: 'author', of: '$ref' }] } }),
          oneRef('inquire'),
        ],
      },

      // Trade: buyer accepts a live offer; both parties confirm; the accept id is the interaction id.
      // R8: an accept that either party released no longer takes the offer.
      accept: {
        all: [
          L('accept.member', member('$author') as Exclude<C, true>),
          L('accept.offer', { is: { claim: '$ref', predicate: 'offer' } }),
          L('accept.not-own', { not: { eq: ['$author', { get: 'author', of: '$ref' }] } }),
          L('accept.not-withdrawn', { not: { exists: { predicate: 'withdraw', refs: '$ref' } } }),
          L('accept.not-taken', { not: { exists: liveAccept('$ref') } }),
          // v2.2 (REDTEAM-3 U3, accept-squatting): a buyer holds at most acceptCap open accepts, and a buyer the seller
          // released may not take the same offer again (every earlier accept of mine on it was not released by the seller).
          L('accept.cap', { count: { predicate: 'accept', author: '$author', unrefBy: ['release', 'complete'] }, op: 'lt', n: { param: 'acceptCap' } }),
          L('accept.not-released-by-seller', {
            eq: [
              { count: { predicate: 'accept', refs: '$ref', author: '$author' } },
              { count: { predicate: 'accept', refs: '$ref', author: '$author', unrefBy: [{ predicate: 'release', author: { get: 'author', of: '$ref' } }] } },
            ],
          }),
          oneRef('accept'),
        ],
      },
      release: {
        all: [
          L('release.accept', { is: { claim: '$ref', predicate: 'accept' } }),
          L('release.party', partyTo('$ref') as Exclude<C, true>),
          // v2.2 (REDTEAM-3 U6): a party may release only while every confirmation so far is their own, so nobody can
          // back out after the other side confirmed (and so dodge the rating and relist the item).
          L('release.open', { eq: [{ count: { predicate: 'complete', refs: '$ref' } }, { count: { predicate: 'complete', refs: '$ref', author: '$author' } }] }),
          L('release.once', { not: { exists: { predicate: 'release', refs: '$ref' } } }),
          oneRef('release'),
        ],
      },
      complete: {
        all: [
          L('complete.accept', { is: { claim: '$ref', predicate: 'accept' } }),
          L('complete.party', partyTo('$ref') as Exclude<C, true>),
          L('complete.once', { not: { exists: { predicate: 'complete', refs: '$ref', author: '$author' } } }),
          L('complete.not-released', { not: { exists: { predicate: 'release', refs: '$ref' } } }),
          oneRef('complete'),
        ],
      },

      // A rating must point at a completed interaction the author was party to, once, about the other party.
      // #18: no rating between vouch-linked members (self-dealing through a puppet earns nothing).
      rate: {
        all: [
          L('rate.accept', { is: { claim: '$ref', predicate: 'accept' } }),
          // v2.2 (REDTEAM-3 U6): both confirmed, or the author confirmed at least rampAfter ago and nobody released: a
          // silent counterparty no longer blocks the rating (as R16 did for the ramp).
          L('rate.both-confirmed', {
            any: [
              { count: { predicate: 'complete', refs: '$ref' }, distinct: 'author', op: 'gte', n: { lit: 2 } },
              L('rate.confirmed-and-waited', {
                all: [
                  { exists: { predicate: 'complete', refs: '$ref', author: '$author', admittedBefore: { sub: ['$now', { param: 'rampAfter' }] } } },
                  { not: { exists: { predicate: 'release', refs: '$ref' } } },
                ],
              }),
            ],
          }),
          L('rate.party', partyTo('$ref') as Exclude<C, true>),
          L('rate.about-counterparty', { any: [{ eq: ['$subject', buyerOf('$ref')] }, { eq: ['$subject', sellerOf('$ref')] }] }),
          L('rate.not-self', { not: { eq: ['$subject', '$author'] } }),
          L('rate.once', { not: { exists: { predicate: 'rate', refs: '$ref', author: '$author' } } }),
          L('rate.min', { cmp: [{ get: 'body.score', of: '$self' }, 'gte', { lit: 1 }] }),
          L('rate.max', { cmp: [{ get: 'body.score', of: '$self' }, 'lte', { lit: 5 }] }),
          L('rate.not-vouch-linked', { not: vouchLinked('$author', '$subject') }),
          oneRef('rate'),
        ],
      },
      // R4, R10: the person a rating, a report or a decision is about may answer it on the record, once.
      respond: {
        all: [
          L('respond.target', {
            any: [{ is: { claim: '$ref', predicate: 'rate' } }, { is: { claim: '$ref', predicate: 'report' } }, { is: { claim: '$ref', predicate: 'decide' } }],
          }),
          L('respond.subject', { eq: ['$author', { get: 'subject', of: '$ref' }] }),
          L('respond.once', { not: { exists: { predicate: 'respond', refs: '$ref', author: '$author' } } }),
          oneRef('respond'),
        ],
      },
      // R4: the person a decision is about may appeal it once; a different steward decides the appeal (see decide).
      // R17 (v2.1): an appeal decision is final. A decision whose ref0 is an appeal cannot itself be appealed, so the
      // chain is one deep and the original decider can never sit on an appeal of their own decision at any depth.
      appeal: {
        all: [
          L('appeal.decision', { is: { claim: '$ref', predicate: 'decide' } }),
          L('appeal.first-instance', { not: { is: { claim: { get: 'ref0', of: '$ref' }, predicate: 'appeal' } } }),
          L('appeal.subject', { eq: ['$author', { get: 'subject', of: '$ref' }] }),
          L('appeal.once', { not: { exists: { predicate: 'appeal', refs: '$ref' } } }),
          oneRef('appeal'),
        ],
      },

      // Governance: report → friction (via offerLimit) → case evidence → human decision → consequence (via offer rule).
      // #18: only attested members file, and at most reportCap per reportWindow. A vouched-only member asks their voucher.
      report: {
        all: [
          L('report.member', member('$author') as Exclude<C, true>),
          L('report.subject-member', member('$subject') as Exclude<C, true>),
          L('report.not-self', { not: { eq: ['$subject', '$author'] } }),
          // v2.2 (REDTEAM-3 U1, ghost reports): every steward who is a member must be able to see the report, so one of
          // them can close it. A report addressed to its author alone used to count for friction for ever.
          L('report.stewards-see', {
            any: [
              { eq: [{ get: 'audience', of: '$self' }, { lit: 'scope' }] },
              { each: { in: [{ get: 'author', of: '$item' }, { get: 'audience', of: '$self' }] }, over: { match: { predicate: 'join', authorIn: { param: 'stewards' } } } },
            ],
          }),
          L('report.attested', attested('$author') as Exclude<C, true>),
          L('report.cap', { count: recent('report', 'reportWindow'), op: 'lt', n: { param: 'reportCap' } }),
        ],
      },
      // Evidence enters a case as a signed claim under body.$claim. The promoter is a party to the case, and (#14)
      // the message is between the two parties to the case; that also makes the promoter a party to the message.
      promote: {
        all: [
          L('promote.report', { is: { claim: '$ref', predicate: 'report' } }),
          L('promote.case-party', { any: [{ eq: ['$author', { get: 'author', of: '$ref' }] }, { eq: ['$author', { get: 'subject', of: '$ref' }] }] }),
          L('promote.between-parties', { all: [embeddedParty({ get: 'author', of: '$ref' }), embeddedParty({ get: 'subject', of: '$ref' })] }),
          oneRef('promote'),
        ],
      },
      flag: { all: [L('flag.triage', { in: ['$author', { param: 'triage' }] }), L('flag.report', { is: { claim: '$ref', predicate: 'report' } }), oneRef('flag')] },
      // A decision either closes one or more reports (R6: every ref is an undecided report about the subject, none filed
      // by the deciding steward, R7) or answers an appeal (R4: refs = [appeal, the decision appealed], by a steward other
      // than the one who made it). A decision that another decision refers to is superseded (see activeSanction).
      decide: {
        any: [
          L('decide.reports', {
            all: [
              L('decide.steward', steward),
              L('decide.not-self', { not: { eq: ['$author', '$subject'] } }),
              L('decide.each-report', {
                each: {
                  all: [
                    { is: { claim: '$item', predicate: 'report' } },
                    { eq: ['$subject', { get: 'subject', of: '$item' }] },
                    { not: { eq: ['$author', { get: 'author', of: '$item' }] } },
                    { not: { exists: { predicate: 'decide', mentions: '$item' } } }, // closed by any decision that lists it
                  ],
                },
                over: 'refs',
              }),
            ],
          }),
          L('decide.appeal', {
            all: [
              L('decide.steward', steward),
              L('decide.not-self', { not: { eq: ['$author', '$subject'] } }),
              L('decide.appealed', { is: { claim: '$ref', predicate: 'appeal' } }),
              L('decide.original', { eq: [{ get: 'ref1', of: '$self' }, { get: 'ref0', of: '$ref' }] }),
              L('decide.same-subject', { eq: ['$subject', { get: 'subject', of: { get: 'ref0', of: '$ref' } }] }),
              L('decide.other-steward', { not: { eq: ['$author', { get: 'author', of: { get: 'ref0', of: '$ref' } }] } }),
              L('decide.once', { not: { exists: { predicate: 'decide', refs: '$ref' } } }),
              // v2.2 (REDTEAM-3 K2): an appeal decision carries the EFFECTIVE outcome. Keeping the sanction means
              // restating it (same outcome, until no later than the original); lifting it means a liftOutcomes value. An
              // "upheld" pseudo-outcome used to supersede, and so lift, the sanction it meant to keep.
              L('decide.appeal.outcome', {
                any: [
                  {
                    all: [
                      { eq: [{ get: 'body.outcome', of: '$self' }, { get: 'body.outcome', of: { get: 'ref0', of: '$ref' } }] },
                      { cmp: [{ get: 'body.until', of: '$self' }, 'lte', { get: 'body.until', of: { get: 'ref0', of: '$ref' } }] },
                    ],
                  },
                  { in: [{ get: 'body.outcome', of: '$self' }, { param: 'liftOutcomes' }] },
                ],
              }),
              twoRefs('decide.appeal'),
            ],
          }),
        ],
      },

      // v2.2 (UX R3, REDTEAM-3 U5): a member names themselves on the record. The UX shows the latest one next to a
      // key fingerprint; nothing else in the policy reads it.
      profile: {
        all: [L('profile.member', member('$author') as Exclude<C, true>), L('profile.self', { eq: ['$subject', '$author'] }), L('profile.no-refs', { eq: [{ get: 'ref0', of: '$self' }, { lit: null }] })],
      },

      // Donation intent. Settlement would be another actor's attestation (a payment issuer); none in week 1.
      pledge: { all: [L('pledge.member', member('$author') as Exclude<C, true>), L('pledge.positive', { cmp: [{ get: 'body.amountInr', of: '$self' }, 'gt', { lit: 0 }] })] },
    },
  };
}
